import { AfterViewInit, ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges, TemplateRef, ViewChild, Output, EventEmitter } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { FormControl } from '@angular/forms';
import { Filters, SelectOptions, ColumnConfig } from 'src/shared/models';
import { MatPaginatorIntl } from "@angular/material/paginator";
import moment from 'moment';
import { DateRangeComponent } from '../../atoms/date-range/date-range.component';
import { LanguageService } from 'src/shared/services/language.service';
import { LoaderService } from 'src/shared/services/loader.service';
import { debounceTime, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'organism-datatable',
  templateUrl: './datatable.component.html',
  styleUrls: ['./datatable.component.scss'],
})
export class DatatableComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() JSONdata: Array<any> = [];
  @Input() displayedColumns: string[] = [];
  @Input() displayedColumnsConfig: ColumnConfig[] = []; // Nueva propiedad para columnas con tooltips
  @Input() columnsFiltered?: Filters[] = [];
  @Input() showPaginator: boolean = true;
  @Input() isShowFiltersRow: boolean = true;
  @Input() actionsTemplate: TemplateRef<any> | null = null;
  @Input() disableInternalLoader: boolean = false; // Nueva propiedad para desactivar el loader interno
  @Input() maxMonthsRange?: number; // Cantidad máxima de meses que se pueden seleccionar en el filtro de calendario

  @Output() dateRangeChanged = new EventEmitter<{ start: string | null; end: string | null }>();
  @Output() filteredDataChange = new EventEmitter<number>();

  @ViewChild(MatPaginator) paginator?: MatPaginator;

  public dataSource = new MatTableDataSource(this.JSONdata);
  public columnOptions: { [key: string]: SelectOptions[] } = {};
  public filterColumns: string[] = [];
  public filters: { [key: string]: string } = {};
  public selectValues: { [key: string]: FormControl } = {};
  public autocompleteValues: { [key: string]: FormControl } = {};
  public dateValues: { [key: string]: FormControl } = {};
  public currentLanguage: string = "";
  public isLoadingData: boolean = false;
  private destroy$ = new Subject<void>();
  private filterSubject = new Subject<void>();

  @ViewChild('dateRangeRef') dateRangeComponent!: DateRangeComponent;

  constructor(private matPaginatorIntl: MatPaginatorIntl, private languageService:LanguageService, private loaderService:LoaderService, private cdr: ChangeDetectorRef) {}

  ngOnInit() {
    this.languageService.currentLanguage$.subscribe(lang => {
      this.currentLanguage = lang;
      this.onTranslatePaginated(this.currentLanguage);
    });

    this.columnsFiltered && this.columnsFiltered.forEach((item) => {
      if(this.isShowFiltersRow){
        this.getOptions(item);

        this.selectValues[item.title] = new FormControl(null);
        this.dateValues[item.title] = new FormControl(null);
        this.autocompleteValues[item.title] = new FormControl(null);
      }
    });

    this.dataSource.data = this.JSONdata;
    // Suscripción al filtro con debounce: oculta el loader y refresca la interfaz cuando termina el filtrado
    this.filterSubject.pipe(takeUntil(this.destroy$), debounceTime(1000)).subscribe(() => {
      this.isLoadingData = false;
      // Solo ocultar el loader si no está desactivado
      if (!this.disableInternalLoader) {
        this.loaderService.hide();
      }
      this.cdr.detectChanges();
    });
  }

  ngAfterViewInit() {
    if (this.showPaginator && this.paginator) {
      this.dataSource.paginator = this.paginator;
    }

    this.applyFilters();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['JSONdata'] && changes['JSONdata'].currentValue) {
      this.isLoadingData = true;
      this.dataSource.data = this.JSONdata;
      this.columnsFiltered && this.columnsFiltered.forEach((item) => {
        this.getOptions(item);
      });
      // Aplicar filtros y emitir evento cuando los datos se cambien desde el componente padre
      this.applyFilters();
      this.dataSource.filter = JSON.stringify(this.filters);

      // Usar Promise.resolve para ejecutar en el siguiente ciclo
      Promise.resolve().then(() => {
        this.isLoadingData = false;
        this.emitFilteredCount();
        this.cdr.detectChanges();
      });
    } else {
      this.applyFilterWithLoading();
    }
  }

  getIdByColumn(filter: Filters, index: number): string {
    const columnId = `${filter.title}_filter_${index}`;
    this.filterColumns[index] = columnId;
    return columnId;
  }

  onRemoveAllFilters(){
    for (const column in this.selectValues) {
      if (this.selectValues.hasOwnProperty(column)) {
        this.filters[column] = '';
        this.dateRangeComponent.range.setValue({start: null, end: null});
        this.selectValues[column].setValue(null);
        this.autocompleteValues[column].reset();
      }
    }

    this.dateRangeChanged.emit({
      start: null,
      end: null
    });

    this.applyFilterWithLoading();
  }

  getOptions(filter: Filters): void {
    const uniqueColumnValues  = [...new Set(this.JSONdata.map((data) => data[filter.title]))];

    this.columnOptions[filter.title] = uniqueColumnValues.filter(value => value !== null && value !== undefined)
    .map((value) => ({
      title: value,
      value,
    }))
    .sort((a, b) => {
      // Ordenar alfabéticamente sin distinguir mayúsculas/minúsculas y considerando números
      return String(a.title).localeCompare(String(b.title), this.currentLanguage, { sensitivity: 'base', numeric: true });
    });
  }

  onChangeSelect(value: any, column: string) {
    this.selectValues[column].setValue(value);
    this.filters[column] = this.selectValues[column].value;

    this.applyFilterWithLoading();
  }

  onChangeAutocomplete(value: string, column: string) {
    this.autocompleteValues[column].setValue(value);
    this.filters[column] = this.autocompleteValues[column].value;

    this.applyFilterWithLoading();
  }

  onChangeDateRange(event: { startDate: string; endDate: string }, column: string) {
    this.dateValues[column].setValue({ start: event.startDate, end: event.endDate });

    this.filters[column] = JSON.stringify({
      startDate: event.startDate,
      endDate: event.endDate,
    });

    this.dateRangeChanged.emit({
      start: event.startDate,
      end: event.endDate
    });

    this.applyFilterWithLoading();
  }

  onRemoveFilter(filter:Filters){
    this.filters[filter.title] = '';
    this.selectValues[filter.title].setValue(null);
    this.autocompleteValues[filter.title].reset();

    (filter.filterType === 'Calendar') ?
    this.dateRangeComponent.range.setValue({start: null, end: null}) : null;

    if (filter.filterType === 'Calendar') {
      this.dateRangeChanged.emit({
        start: null,
        end: null
      });
    }

    this.applyFilterWithLoading();
  }

  public getFilteredData(): any[] {
    return this.dataSource.filteredData;
  }

  public getColumnTooltip(columnName: string): string | undefined {
    const columnConfig = this.displayedColumnsConfig.find(col => col.name === columnName);
    return columnConfig?.tooltip;
  }

  public getColumnTooltipText(columnName: string): string {
    const columnConfig = this.displayedColumnsConfig.find(col => col.name === columnName);
    return columnConfig?.tooltip || '';
  }

  private applyFilters() {
    if (!this.isShowFiltersRow || !this.columnsFiltered?.length) {
      this.dataSource.filterPredicate = () => true; // No realizar el filtro
      return;
    }

    this.dataSource.filterPredicate = (data: any, filter: string): boolean => {
      const filters = JSON.parse(filter);

      for (let column in filters) {
        const columnValue = data[column];
        const filterValue = filters[column];

        if (filterValue) {
          try {
            const parsedFilter = JSON.parse(filterValue);

            if (parsedFilter?.startDate && parsedFilter?.endDate) {
              const rowDate = moment(columnValue, 'DD-MM-YYYY');
              const startDate = moment(parsedFilter.startDate, 'DD-MM-YYYY');
              const endDate = moment(parsedFilter.endDate, 'DD-MM-YYYY');

              if (!rowDate.isBetween(startDate, endDate, undefined, '[]')) {
                return false;
              }
            }
          } catch (e) {
            if (filterValue && columnValue !== filterValue) {
              return false;
            }
          }
        }
      }

      return true;
    };
  }

  private onTranslatePaginated(lang:string){
    switch(lang){
      case 'en':
        this.matPaginatorIntl.itemsPerPageLabel = "Items per page";
        this.matPaginatorIntl.firstPageLabel = "First page";
        this.matPaginatorIntl.previousPageLabel = "Previous page";
        this.matPaginatorIntl.nextPageLabel = "Next page";
        this.matPaginatorIntl.lastPageLabel = "Last page";
        this.matPaginatorIntl.getRangeLabel = (page: number, pageSize: number, length: number) => {
          if (length === 0 || pageSize === 0) {
            return `0 of ${length.toLocaleString('es-CO')}`;
          }
          const startIndex = page * pageSize;
          const endIndex = startIndex < length
            ? Math.min(startIndex + pageSize, length)
            : startIndex + pageSize;
          return `${(startIndex + 1).toLocaleString('es-CO')} - ${endIndex.toLocaleString('es-CO')} of ${length.toLocaleString('es-CO')}`;
        }
      break;
      case 'es':
        this.matPaginatorIntl.itemsPerPageLabel = "Registros por página";
        this.matPaginatorIntl.firstPageLabel = "Primera página";
        this.matPaginatorIntl.previousPageLabel = "Página anterior";
        this.matPaginatorIntl.nextPageLabel = "Siguiente página";
        this.matPaginatorIntl.lastPageLabel = "Última página";

        this.matPaginatorIntl.getRangeLabel = (page: number, pageSize: number, length: number) => {
          if (length === 0 || pageSize === 0) {
            return `0 de ${length.toLocaleString('es-CO')}`;
          }
          const startIndex = page * pageSize;
          const endIndex = startIndex < length
            ? Math.min(startIndex + pageSize, length)
            : startIndex + pageSize;
          return `${(startIndex + 1).toLocaleString('es-CO')} - ${endIndex.toLocaleString('es-CO')} de ${length.toLocaleString('es-CO')}`;
        }
      break;
    }

    if (this.paginator) {
      this.paginator._intl.changes.next();
    }
  }

  private applyFilterWithLoading() {
    if (!this.isShowFiltersRow || !this.columnsFiltered?.length) {
      this.dataSource.filterPredicate = () => true; // nunca filtrar
      this.emitFilteredCount();
      return;
    }

    this.isLoadingData = true;

    // Solo mostrar el loader interno si no está desactivado
    if (!this.disableInternalLoader) {
      this.loaderService.show();
    }

    // Aplicar filtro
    this.dataSource.filter = JSON.stringify(this.filters);
    Promise.resolve().then(() => {
      this.emitFilteredCount();
    });
    this.filterSubject.next();
  }

  private emitFilteredCount(): void {
    const count = this.dataSource.filteredData.length;
    this.filteredDataChange.emit(count);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
